import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Copy, DollarSign, CreditCard } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const PAYMENT_OPTIONS = [
  { label: "Zelle", value: "brett@screenzz.com", note: "It will show as Kairos Media LLC. That’s us!" },
  { label: "Venmo", value: "@screenzz" },
];

const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;

// Format E.164 → "(951) 290-8133"
const fmtPhone = (p) => {
  if (!p) return "";
  const d = String(p).replace(/\D/g, "").replace(/^1/, "").slice(-10);
  if (d.length !== 10) return String(p);
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};
// Build a tel: / sms: href in +1 format
const telHref = (p) => {
  const d = String(p || "").replace(/\D/g, "").replace(/^1/, "").slice(-10);
  return d.length === 10 ? `+1${d}` : String(p || "");
};

export default function InvoicePayView() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/invoices/${invoiceId}/public-view`);
        if (data.redirect_to_receipt) {
          navigate(`/receipt/${invoiceId}`, { replace: true });
          return;
        }
        if (!cancelled) setInvoice(data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || "Invoice not available");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [invoiceId, navigate]);

  const copy = (v) => {
    navigator.clipboard?.writeText(v);
    toast.success(`Copied "${v}"`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center" data-testid="invoice-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#207024]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="invoice-error">
        <div className="max-w-md text-center">
          <div className="text-lg font-bold text-zinc-800 mb-2">Invoice not available</div>
          <div className="text-sm text-zinc-500">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-16" data-testid="invoice-pay-view">
      <div className="max-w-lg mx-auto px-4 pt-8">
        <div className="flex flex-col items-center text-center gap-3 mb-6">
          <img
            src="https://customer-assets.emergentagent.com/job_tech-dispatch-31/artifacts/a6ta9f48_SCREENZZ-LOGO-RGB_TextOnly.png"
            alt="ScreenzZ"
            className="h-12 w-auto"
          />
          <h1 className="text-2xl font-bold text-zinc-900">Invoice for {invoice.customer_name}</h1>
        </div>

        {/* HERO — total due + primary action */}
        <div className="rounded-2xl bg-gradient-to-br from-[#207024] to-[#164e19] text-white shadow-lg p-6 text-center">
          <div className="text-xs uppercase tracking-widest text-emerald-100 font-semibold">Total due</div>
          <div className="text-5xl font-bold mt-1" data-testid="invoice-total">{fmt(invoice.total)}</div>
          <div className="text-sm text-emerald-100 mt-1 font-mono">{invoice.invoice_number}</div>
        </div>

        {/* PAYMENT OPTIONS — the main CTA */}
        <div className="bg-white rounded-2xl border-2 border-[#207024] shadow-sm p-5 mt-5">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="w-5 h-5 text-[#207024]" />
            <div className="text-base font-bold text-zinc-900">Ready to pay?</div>
          </div>
          <div className="space-y-2">
            {PAYMENT_OPTIONS.map(o => (
              <button
                key={o.label}
                onClick={() => copy(o.value)}
                data-testid={`invoice-pay-${o.label.toLowerCase()}`}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 border-zinc-200 hover:border-[#207024] hover:bg-zinc-50 transition-colors"
              >
                <span className="text-sm text-left">
                  <span className="font-bold text-zinc-800">{o.label}:</span> <span className="font-mono break-all">{o.value}</span>
                  {o.note && <span className="block text-xs text-zinc-500 mt-0.5" data-testid={`invoice-pay-${o.label.toLowerCase()}-note`}>{o.note}</span>}
                </span>
                <Copy className="w-4 h-4 text-zinc-400" />
              </button>
            ))}
            <a
              href={`sms:${telHref(invoice.business_phone)}?body=I'd like to pay invoice ${invoice.invoice_number} by card`}
              data-testid="invoice-pay-card"
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 border-zinc-200 hover:border-[#207024] hover:bg-zinc-50 transition-colors no-underline"
            >
              <span className="text-sm text-zinc-800">
                <span className="font-bold">Card:</span>{" "}
                <span>tap to request a secure payment link</span>
              </span>
              <CreditCard className="w-4 h-4 text-zinc-400" />
            </a>
          </div>
        </div>

        {/* Line items */}
        <div className="bg-white rounded-2xl border-2 border-zinc-200 shadow-sm p-5 mt-5 space-y-3">
          <div className="text-sm font-bold text-zinc-800 mb-2">Details</div>
          {invoice.line_items.map((li, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="text-zinc-800">
                {li.name} <span className="text-zinc-400">× {li.quantity}</span>
              </span>
              <span className="font-semibold">{fmt(li.line_total)}</span>
            </div>
          ))}
          <div className="border-t border-zinc-200 pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span>{fmt(invoice.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Tax</span><span>{fmt(invoice.tax)}</span></div>
            <div className="flex justify-between text-lg font-bold text-[#207024] pt-1"><span>Total Due</span><span>{fmt(invoice.total)}</span></div>
          </div>
        </div>

        <p className="text-center text-xs text-zinc-500 mt-6">
          Questions? Call or text{" "}
          <a href={`tel:${telHref(invoice.business_phone)}`} className="text-[#207024] font-semibold">
            {fmtPhone(invoice.business_phone) || "us"}
          </a>{" "}
          — we&apos;re happy to help.
        </p>
      </div>
    </div>
  );
}
